import React, { Fragment, useState } from 'react';
import { DragDropContext, Droppable as DroppableBase, Draggable as DraggableBase } from '@hello-pangea/dnd';
import { MAX_TASK_STEP_COUNT } from '../../../../constants/constants';
import InputText from '../../../Common/InputText';

import './CreateTaskForm.scss';
import { Task } from '../../Interfaces/Types';

// @hello-pangea/dnd ships a duplicate @types/react that conflicts with the app's
// React types (ReactNode bigint mismatch), so cast the components to clear the
// spurious JSX-element-type errors without losing runtime behavior.
const Droppable = DroppableBase as unknown as React.FC<any>;
const Draggable = DraggableBase as unknown as React.FC<any>;

//#TODO ADD CIRCLE ICON
interface StepsComponentProps {
	currentTask: Task;
	onFormChange: Function;
}

export const StepsComponentForm = ({ currentTask, onFormChange }: StepsComponentProps) => {
	// track which single step is in edit mode (by ordinal); null = none
	const [editingOrdinal, setEditingOrdinal] = useState<string | null>(null);

	const isEditing = (step) => editingOrdinal !== null && String(step.ordinal) === editingOrdinal;

	const getListStyle = (isDraggingOver) => ({
		background: isDraggingOver ? '#eee' : 'white',
		borderRadius: '0.25rem',
	});

	// tried moving this to scss but it just gets overridden from within the component
	const getItemStyle = (isDragging, draggableStyle) => ({
		userSelect: 'none',
		lineHeight: 1,
		margin: `0 0 8px 0`,
		background: 'white',
		border: isDragging ? '2px solid #03a9f4' : '',
		// styles we need to apply on draggables
		...draggableStyle,
	});

	const onDragEnd = (result) => {
		// dropped outside the step list area
		if (!result.destination) {
			return;
		}

		const steps = Array.from(currentTask.steps);
		const [removed] = steps.splice(result.source.index, 1);

		steps.splice(result.destination.index, 0, removed);

		// Reindex all steps with 1-based ordinals so baseline and new steps
		// stay consistent after any reorder. Keep ordinals as strings so the
		// draggableId type never changes between renders (react-dnd requires string).
		for (let i = 0; i < steps.length; i++) {
			if (steps[i]) {
				steps[i].ordinal = (i + 1).toString();
			}
		}
		setEditingOrdinal(null);
		onFormChange('steps', steps);
	};

	const onUpdateStepName = (step, e) => {
		let steps = currentTask.steps.slice();
		const index = steps.findIndex((x) => x.ordinal === step.ordinal);
		step.name = e.target.value;
		steps.splice(index, 1, step);
		onFormChange('steps', steps);
	};

	const onRemoveStep = (step) => {
		const steps = currentTask.steps.slice();
		const index = steps.findIndex((x) => x.ordinal === step.ordinal);
		// removing it from this array will mark it IsActive = false on the server during save (if it existed in the db)
		steps.splice(index, 1);
		// update the ordinal of any other steps after it in the array.
		// Keep ordinals as strings so the type stays consistent with onAddStep
		// and onDragEnd (draggableId requires string).
		for (let i = index; i < steps.length; i++) {
			steps[i].ordinal = (i + 1).toString();
		}
		onFormChange('steps', steps);
	};

	const onAddStep = () => {
		const steps = [...currentTask.steps];

		// ordinals can arrive as strings (from load) — coerce before incrementing
		const lastOrdinal = steps.length > 0 ? Number(steps[steps.length - 1].ordinal) : 0;
		const nextOrdinal = (lastOrdinal + 1).toString();

		const newStep = {
			id: 0,
			isCompleted: false,
			name: '',
			isActive: false,
			ordinal: nextOrdinal,
			baselineTaskId: currentTask.id,
		};

		steps.push(newStep);

		onFormChange('steps', steps);
		setEditingOrdinal(nextOrdinal);
	};

	const editCompleted = () => {
		setEditingOrdinal(null);
	};

	// Enter on a step name commits it and immediately starts a new step,
	// instead of bubbling up and toggling the surrounding accordion sections.
	const onStepNameKeyDown = (step, e) => {
		if (e.key === 'Enter' || e.keyCode === 13) {
			e.preventDefault();
			if (step.name && step.name.trim().length > 0 && currentTask.steps?.length < MAX_TASK_STEP_COUNT) {
				onAddStep();
			}
		}
	};

	return (
		<div className='col-12'>
			<ul className='list-group'>
				<li className='step-header list-group-item d-flex justify-content-between align-items-center'>
					Step Details
					{currentTask.steps?.length < MAX_TASK_STEP_COUNT ? (
						<button type='button' className='btn btn-link' onClick={onAddStep}>
							<i className='fa-regular fa-circle-plus'></i>
							<span className='mx-1'>Add Step</span>
						</button>
					) : (
						''
					)}
				</li>
				<DragDropContext onDragEnd={onDragEnd}>
					<Droppable droppableId='droppable'>
						{(provided, snapshot) => (
							<div ref={provided.innerRef} style={getListStyle(snapshot.isDraggingOver)}>
								{currentTask.steps?.map((step, index) => (
									<Draggable key={String(step.ordinal)} draggableId={String(step.ordinal)} index={index}>
										{(provided, snapshot) => (
											<Fragment>
												<div
													className='list-group-item d-flex align-items-center step-row'
													ref={provided.innerRef}
													{...provided.draggableProps}
													{...provided.dragHandleProps}
													style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}>
													<div className='col-1'>
														<div className='task-step-bars'>
															<div className='icon-black custom-size-sup'>
																<span className='fa fa-bars'></span>
															</div>
														</div>
													</div>
													<div className={`step-name-cell${isEditing(step) ? ' editing' : ''}`}>
														{isEditing(step) ? (
															<InputText
																customClass='mb-0'
																name={`taskStep${index + 1}`}
																type='text'
																placeholder='Describe this step'
																autoComplete='off'
																ariaLabel='Step name'
																value={step.name}
																isRequired={true}
																isFocused={true}
																errorMessage='Step name required'
																onChange={(e) => onUpdateStepName(step, e)}
																onKeyDown={(e) => onStepNameKeyDown(step, e)}
																maxLength={100}
															/>
														) : (
															step.name
														)}
													</div>
													<div className='step-footer'>
														{isEditing(step) ? (
															<div className='icon-black custom-size-sup' onClick={editCompleted}>
																<i className='fa-regular fa-check'></i>
															</div>
														) : (
															<div className='icon-black custom-size-sup' onClick={() => setEditingOrdinal(String(step.ordinal))}>
																<i className='fa-light fa-pen-to-square'></i>
															</div>
														)}
														<div className='icon custom-size-sup' onClick={() => onRemoveStep(step)}>
															<span className='fa-light fa-trash-can'></span>
														</div>
													</div>
												</div>
											</Fragment>
										)}
									</Draggable>
								))}
								{provided.placeholder}
							</div>
						)}
					</Droppable>
				</DragDropContext>
			</ul>
		</div>
	);
};
